import { useMutation } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useModals } from "react-easy-modals";
import * as yup from "yup";
import { colors, sizes, spacings } from "@/assets/themes";
import { BUTTON } from "@/components/styles/variants/button";
import { useAuth } from "@/modules/hooks/use-auth";
import useToast from "@/modules/hooks/use-toast";
import polyglot from "@/utils/polyglot";
import Alert from "../alert";
import Avatar from "../avatar/index";
import Block from "../block";
import Button from "../button";
import ConditionnalWrapper from "../conditionnal-wrapper";
import Divider from "../divider";
import FormControl from "../form-control";
import Input from "../input";
import Modal from "../modal/index";
import { CardBase } from "../styles/base";
import { Body16, Body18, H2, H3, H4, H5 } from "../text";
import Login from "./login-modal";
import SignUp from "./signup-modal";
import WrongCountryModal from "./wrong-country-modal";

const loginSchema = yup.object({
  email: yup.string().email("Invalid email").required("Email is required"),
});

function AlertMessage({ alertMessage }) {
  return alertMessage ? (
    <Block marginBottom={spacings.s}>
      <Alert.Low>{alertMessage}</Alert.Low>
    </Block>
  ) : null;
}

function Referrer({ referrer }) {
  return referrer ? (
    <CardBase
      css={`
        padding: ${spacings.m};
        margin-bottom: ${spacings.s};
      `}
      flat
      light
    >
      <Block alignItems="center" display="flex">
        <Avatar name={referrer?.first_name} size={sizes.size54} />
        <Block marginLeft={spacings.s}>
          <Body16>{polyglot.t("referral.invited_by")}</Body16>
          <H5>{referrer?.first_name}</H5>
        </Block>
      </Block>
    </CardBase>
  ) : null;
}

const SocialButton = ({ provider, params, redirectionPath, close, preventRedirect = false }) => {
  const modals = useModals();
  const { signIn, redirect } = useAuth();

  const signInMutation = useMutation({
    mutationFn: (values) => signIn(provider.id, values),
    onSuccess: (response) => {
      if (preventRedirect) {
        close(response);
      } else {
        redirect(redirectionPath);
      }
    },
    onError: ({ response }, values) => {
      if (response.data.user_country_code && response.data.domain_country_code) {
        modals.open(WrongCountryModal, {
          originCountryCode: response?.data?.user_country_code,
          domainCountryCode: response?.data?.domain_country_code,
          onSignUp: () =>
            modals.open(
              SignUp,
              {
                redirectionPath,
                params,
                preventRedirect,
                resolve: (result) => {
                  // close this current modal after resolving the promise (and close the main modal)
                  close(result);
                },
              },
              { replace: true }
            ),
          onRedirect: () => redirect(redirectionPath, response?.data),
        });
      } else {
        throw new Error(`Login error: ${error}`);
      }
    },
  });

  return (
    <Button.Medium
      block
      css={`
        color: ${colors.body}!important;
      `}
      isLoading={signInMutation.isPending || signInMutation.isSuccess}
      key={provider.id}
      kind={BUTTON.KIND.SECONDARY}
      LeftComponent={() => (
        <img
          alt={provider.name}
          css={`
            width: ${sizes.size18};
            height: auto;
            margin-right: ${spacings.xs};
          `}
          // eslint-disable-next-line react/no-unknown-property
          src={provider.icon}
        />
      )}
      onClick={() => signInMutation.mutate({ ...params })}
    >
      {polyglot.t("common.continue_with_provider", { provider: provider.name })}
    </Button.Medium>
  );
};

function Main({
  isOpen,
  close,
  redirectionPath,
  params,
  alertMessage,
  referrer,
  preventRedirect = false,
  noModal = false,
}) {
  const { providers, verifyEmail } = useAuth();
  const modals = useModals();

  const verifyEmailMutation = useMutation({
    mutationFn: (values) => verifyEmail(values),
  });

  const onSubmit = async (values) => {
    verifyEmailMutation.mutate(values.email, {
      onSuccess: (data) => {
        let nextModal;
        if (data.data.redirection === "login") {
          nextModal = Login;
        } else {
          nextModal = SignUp;
        }
        modals.open(nextModal, {
          resolve: (result) => close(result),
          user: {
            ...data.data.user,
            email: values.email,
            // password: values.password,
          },
          preventRedirect,
          redirectionPath,
          params,
        });
      },
    });
  };

  return (
    <ConditionnalWrapper
      condition={!noModal}
      wrapper={(children) => (
        <Modal.Small
          animate={modals.action === "push"}
          forceMount
          fullScreenOnMobile
          isOpen={isOpen}
          onClose={() => close(false)}
        >
          {children}
        </Modal.Small>
      )}
    >
      <Formik
        initialValues={{
          email: "",
        }}
        onSubmit={onSubmit}
        validateOnMount
        validationSchema={loginSchema}
      >
        {({ values, handleChange, handleBlur, isValid }) => (
          <div>
            <Modal.Item.Header
              css="position: relative;"
              navBarContent={
                noModal
                  ? undefined
                  : () => (
                      <>
                        <Block
                          borderBottom={`1px solid ${colors.border}`}
                          left="0"
                          paddingY={spacings.sm}
                          pointerEvents="none"
                          position="absolute"
                          right="0"
                          textAlign="center"
                        >
                          <Body16 align="center" strong>
                            {polyglot.t("common.login_or_signup")}
                          </Body16>
                        </Block>
                      </>
                    )
              }
              onClose={noModal ? undefined : () => close(false)}
            />
            <Modal.Item.Wrapper>
              <Block marginBottom={spacings.s} marginTop={noModal ? 0 : spacings.l}>
                <H2>{polyglot.t("common.welcome_to_website")}</H2>
              </Block>
              <Referrer referrer={referrer} />
              <AlertMessage alertMessage={alertMessage} />
              <Form noValidate>
                <Block display="flex" flexDirection="column" gap={spacings.m}>
                  <Block>
                    <FormControl label={polyglot.t("common.email")}>
                      <Input
                        name="email"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder={polyglot.t("common.placeholder.email")}
                        type="email"
                        value={values.email}
                      />
                    </FormControl>
                    <Button.Large
                      block
                      disabled={!isValid}
                      isLoading={verifyEmailMutation.isPending}
                      type="submit"
                    >
                      {polyglot.t("common.continue")}
                    </Button.Large>
                  </Block>
                  <Divider.Cell>{polyglot.t("common.or")}</Divider.Cell>
                  <Block display="flex" flexDirection="column" gap={spacings.s}>
                    {providers.map((provider) => (
                      <SocialButton
                        close={close}
                        key={provider.id}
                        params={params}
                        preventRedirect={preventRedirect}
                        provider={provider}
                        redirectionPath={redirectionPath}
                      />
                    ))}
                  </Block>
                </Block>
              </Form>
            </Modal.Item.Wrapper>
          </div>
        )}
      </Formik>
    </ConditionnalWrapper>
  );
}

export default Main;
